import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Facebook, Instagram } from "lucide-react";
import { NAV } from "@/data/alcott";
import WaitlistDialog from "@/components/WaitlistDialog";

export const AlcottFooter = ({ onJoin }) => {
  const [mailOpen, setMailOpen] = useState(false);
  return (
    <footer className="border-t border-[#0A0A0A]/10 bg-[#FAF9F6] px-5 py-16 md:px-10 md:py-24" data-testid="alcott-footer">
      <div className="mx-auto max-w-[1500px]">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <p className="font-serif text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
              Sybils &amp; Society.
            </p>
            <p className="mt-2 font-body text-[0.7rem] uppercase tracking-[0.24em] text-[#C05C42]">
              A Cultural &amp; Esoteric Review
            </p>
            <p className="mt-4 max-w-sm font-body text-sm leading-relaxed text-[#0A0A0A]/60">
              A refined intersection of heritage, fashion, and the esoteric. We bridge the tangible
              world with hidden realms through two exclusive conduits:{" "}
              <Link
                to="/veil"
                onClick={() => sessionStorage.removeItem("sybil_entered")}
                className="font-semibold italic text-[#0A0A0A] transition-colors hover:text-[#C05C42]"
                data-testid="footer-blurb-sybils-link"
              >
                The Sybils Society
              </Link>
              , our clandestine order for modern matriarchs, and{" "}
              <a
                href="https://thehorizoncircle.co"
                target="_blank"
                rel="noreferrer"
                className="font-semibold italic text-[#0A0A0A] transition-colors hover:text-[#C05C42]"
                data-testid="footer-blurb-horizon-link"
              >
                The Horizon Circle
              </a>
              , our society for elite global mobility and travel.
            </p>
            <button
              onClick={() => setMailOpen(true)}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C05C42] px-6 py-3 font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#FAF9F6] transition-transform duration-300 hover:-translate-y-0.5"
              data-testid="footer-subscribe-btn"
            >
              Join the mailing list <ArrowUpRight size={14} />
            </button>

            <div className="mt-8 flex items-center gap-3" data-testid="footer-socials">
              <a
                href="https://www.facebook.com/share/15QaLxooVCV/"
                target="_blank"
                rel="noreferrer"
                aria-label="Sybils & Society on Facebook"
                title="Follow us on Facebook"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#0A0A0A]/15 text-[#0A0A0A]/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C05C42] hover:bg-[#C05C42] hover:text-[#FAF9F6]"
                data-testid="footer-facebook-link"
              >
                <Facebook size={17} strokeWidth={1.75} />
              </a>
              <a
                href="https://instagram.com/sybils.society"
                target="_blank"
                rel="noreferrer"
                aria-label="Sybils & Society on Instagram"
                title="Follow us on Instagram"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#0A0A0A]/15 text-[#0A0A0A]/60 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#C05C42] hover:bg-[#C05C42] hover:text-[#FAF9F6]"
                data-testid="footer-instagram-link"
              >
                <Instagram size={17} strokeWidth={1.75} />
              </a>
            </div>
          </div>

          <div>
            <p className="font-body text-[0.6rem] uppercase tracking-[0.25em] text-[#C05C42]">Sections</p>
            <ul className="mt-5 space-y-3">
              {NAV.map((l) =>
                l.label === "Lineage" ? (
                  <li key={l.label}>
                    <Link to="/archives#history" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]" data-testid="footer-lineage-link">
                      {l.label}
                    </Link>
                  </li>
                ) : l.external ? (
                  <li key={l.label}>
                    <a href={l.href} target="_blank" rel="noreferrer" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]">
                      {l.label}
                    </a>
                  </li>
                ) : (
                  <li key={l.label}>
                    <Link to={l.to} className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]">
                      {l.label}
                    </Link>
                  </li>
                )
              )}
              <li>
                <Link to="/archives#art-cities" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]" data-testid="footer-art-cities-link">
                  Art & Cities
                </Link>
              </li>
              <li>
                <Link to="/archives#fashion" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]" data-testid="footer-fashion-link">
                  Heritage & Fashion
                </Link>
              </li>
              <li>
                <Link to="/secrets#sacred-scrolls" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]" data-testid="footer-sacred-scrolls-link">
                  Sacred Scrolls
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="font-body text-[0.6rem] uppercase tracking-[0.25em] text-[#C05C42]">The Circles</p>
            <ul className="mt-5 space-y-3">
              <li>
                <Link to="/veil" onClick={() => sessionStorage.removeItem("sybil_entered")} className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]">
                  Sybils Society
                </Link>
              </li>
              <li>
                <a href="https://thehorizoncircle.co" target="_blank" rel="noreferrer" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]">
                  The Horizon Circle
                </a>
              </li>
              <li>
                <a href="https://7gentrust.org" target="_blank" rel="noreferrer" className="font-body text-sm text-[#0A0A0A]/70 transition-colors hover:text-[#0A0A0A]" data-testid="footer-7gentrust-link">
                  7 Generations Trust
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 flex flex-col items-start justify-between gap-4 border-t border-[#0A0A0A]/10 pt-8 md:flex-row md:items-center">
          <p className="font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/40" data-testid="footer-copyright">
            © {new Date().getFullYear()} Sybils & Society · in care of{" "}
            <a
              href="https://niijiglobal.com"
              target="_blank"
              rel="noreferrer"
              className="underline decoration-[#0A0A0A]/20 underline-offset-2 transition-colors hover:text-[#C05C42]"
              data-testid="footer-niiji-link"
            >
              Niiji Consulting Inc
            </a>{" "}
            · All rights reserved
          </p>
          <div className="flex gap-6">
            <Link to="/legal/privacy" className="font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/40 hover:text-[#0A0A0A]">Privacy</Link>
            <Link to="/legal/terms" className="font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/40 hover:text-[#0A0A0A]">Terms</Link>
            <Link to="/legal/accessibility" className="font-body text-[0.65rem] uppercase tracking-[0.2em] text-[#0A0A0A]/40 hover:text-[#0A0A0A]" data-testid="footer-accessibility-link">Accessibility</Link>
          </div>
        </div>
      </div>
      <WaitlistDialog open={mailOpen} onClose={() => setMailOpen(false)} />
    </footer>
  );
};

export default AlcottFooter;
